/**
 * FmDonutChart — share of a whole. Species slices colour by species code; any
 * other slice by tone or the neutral sequence. The centre names the active
 * slice (or the largest) and its share.
 */
import React from 'react';
import { Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import type { FmDonutChartSpec } from './chartTypes';
import { FmChartTooltip } from './FmChartChrome';
import { donutShare } from './chartData';
import { resolveSeriesColor } from './seriesColor';
import './FmDonutChart.css';

export type FmDonutChartProps = Omit<FmDonutChartSpec, 'kind'> & {
  'data-testid'?: string;
};

export const FmDonutChart: React.FC<FmDonutChartProps> = ({
  slices,
  activeId,
  'data-testid': testId,
}) => {
  const visible = slices.filter((slice) => slice.value > 0);
  const total = visible.reduce((sum, slice) => sum + slice.value, 0);
  if (visible.length === 0 || total <= 0) return null;
  const largest = visible.reduce((top, slice) => (slice.value > top.value ? slice : top));
  const active = visible.find((slice) => slice.id === activeId) ?? largest;
  const data = visible.map((slice) => ({
    id: slice.id,
    label: slice.label,
    value: slice.value,
    fill: resolveSeriesColor(slice.color),
  }));
  return (
    <figure className="fm-donut-chart" data-testid={testId} data-chart-kind="donut">
      <div className="fm-donut-chart__plot">
        <ResponsiveContainer
          width="100%"
          height="100%"
          minWidth={1}
          minHeight={1}
          initialDimension={{ width: 720, height: 320 }}
        >
          <PieChart accessibilityLayer>
            <Tooltip content={<FmChartTooltip />} />
            <Pie
              data={data}
              dataKey="value"
              nameKey="label"
              innerRadius="58%"
              outerRadius="82%"
              paddingAngle={2}
              stroke="var(--fm-chart-halo)"
              isAnimationActive={false}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="fm-donut-chart__center" aria-hidden="true">
          <strong>{donutShare(active.value, total)}</strong>
          <span>{active.label}</span>
        </div>
      </div>
    </figure>
  );
};
